<template>
    <section id="how-it-works" class="py-20 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center max-w-3xl mx-auto mb-16">
                <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full mb-4 uppercase text-sm tracking-wider font-medium">
                    {{ $t('howItWorks.badge') }}
                </div>
                <h2 class="text-3xl sm:text-4xl mb-4">
                    {{ $t('howItWorks.title') }}
                </h2>
                <p class="text-lg text-muted-foreground">
                    {{ $t('howItWorks.subtitle') }}
                </p>
            </div>

            <div class="grid md:grid-cols-3 gap-8 lg:gap-12">
                <!-- Step 1 -->
                <div class="relative group">
                    <div class="space-y-4">
                        <div class="text-6xl text-foreground opacity-10 group-hover:opacity-15 transition-opacity">01</div>
                        <div class="w-12 h-1 bg-accent rounded-full"></div>
                        <h3 class="text-xl">{{ $t('howItWorks.step1.title') }}</h3>
                        <p class="text-muted-foreground">
                            {{ $t('howItWorks.step1.description') }}
                        </p>
                    </div>
                    <div class="hidden md:block absolute top-8 -right-6 lg:-right-8 text-muted-foreground/20">
                        <ArrowRightIcon class="size-6" />
                    </div>
                </div>

                <!-- Step 2 -->
                <div class="relative group">
                    <div class="space-y-4">
                        <div class="text-6xl text-foreground opacity-10 group-hover:opacity-15 transition-opacity">02</div>
                        <div class="w-12 h-1 bg-accent rounded-full"></div>
                        <h3 class="text-xl">{{ $t('howItWorks.step2.title') }}</h3>
                        <p class="text-muted-foreground">
                            {{ $t('howItWorks.step2.description') }}
                        </p>
                    </div>
                    <div class="hidden md:block absolute top-8 -right-6 lg:-right-8 text-muted-foreground/20">
                        <ArrowRightIcon class="size-6" />
                    </div>
                </div>

                <!-- Step 3 -->
                <div class="relative group">
                    <div class="space-y-4">
                        <div class="text-6xl text-foreground opacity-10 group-hover:opacity-15 transition-opacity">03</div>
                        <div class="w-12 h-1 bg-accent rounded-full"></div>
                        <h3 class="text-xl">{{ $t('howItWorks.step3.title') }}</h3>
                        <p class="text-muted-foreground">
                            {{ $t('howItWorks.step3.description') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Enterprise Benefits Panel -->
            <div class="mt-16 bg-muted/50 border border-border rounded-2xl p-8 md:p-12">
                <div class="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h3 class="text-2xl sm:text-3xl mb-4">
                            {{ $t('howItWorks.enterprise.title') }}
                        </h3>
                        <p class="text-muted-foreground mb-6">
                            {{ $t('howItWorks.enterprise.description') }}
                        </p>
                        <ul class="space-y-3">
                            <li class="flex items-start gap-3">
                                <div class="size-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                                    <ArrowRightIcon class="size-3 text-white" />
                                </div>
                                <div>
                                    <div class="font-medium mb-1">{{ $t('howItWorks.enterprise.performance.title') }}</div>
                                    <div class="text-sm text-muted-foreground">{{ $t('howItWorks.enterprise.performance.description') }}</div>
                                </div>
                            </li>
                            <li class="flex items-start gap-3">
                                <div class="size-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                                    <ArrowRightIcon class="size-3 text-white" />
                                </div>
                                <div>
                                    <div class="font-medium mb-1">{{ $t('howItWorks.enterprise.security.title') }}</div>
                                    <div class="text-sm text-muted-foreground">{{ $t('howItWorks.enterprise.security.description') }}</div>
                                </div>
                            </li>
                            <li class="flex items-start gap-3">
                                <div class="size-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                                    <ArrowRightIcon class="size-3 text-white" />
                                </div>
                                <div>
                                    <div class="font-medium mb-1">{{ $t('howItWorks.enterprise.support.title') }}</div>
                                    <div class="text-sm text-muted-foreground">{{ $t('howItWorks.enterprise.support.description') }}</div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div class="aspect-video bg-muted rounded-xl overflow-hidden border border-border shadow-lg">
                        <img
                            src="@/assets/reliability-standards.webp"
                            :alt="$t('howItWorks.enterprise.imageAlt')"
                            class="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ArrowRightIcon } from '@heroicons/vue/24/outline';

export default defineComponent({
    name: 'HowItWorksSection',
    components: {
        ArrowRightIcon
    }
});
</script>
